import { createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { IS_DEVELOPMENT } from '~/modules/config';
import { generateBaseUrl } from '~/modules/helpers/routingHelper';
import { Routes } from '~/modules/routes';
import { ListingResponse } from '~/modules/services/listingService';
import { generateMediaUrl } from '~/modules/utilities/asset_utils';
import { logger } from '~/modules/utilities/logger';
import { SearchResultListing } from '~/schemas/search-schema-v1';
import { RecentItem, RecentItemService } from '../services/recentItemService';
import { listingStore, selectListing, selectListings } from './listingStore';
import { userStore } from './userStore';

const recentItemService = new RecentItemService();

export interface RecentItemState extends State {
    recentItems: RecentItem[];
}

export type LeftOffRecentItem = Omit<
    SearchResultListing,
    | 'context'
    | 'reviewsAmount'
    | 'averageRating'
    | 'type'
    | 'tzId'
    | 'appExchangeId'
    | 'pricing'
    | 'chatEnabled'
    | 'sponsored'
>;

export class HydratedSearchResult {
    listings: ListingResponse[];
    totalSearchResults: number;

    constructor(listings = [], totalSearchResults = 0) {
        return {
            listings: listings,
            totalSearchResults: totalSearchResults,
        };
    }
}

export interface HydratedRecentItem {
    listing: ListingResponse;
    timestamp: number;
}

const initialState: RecentItemState = {
    recentItems: [],
};

export const selectRawRecentItems = (state: RecentItemState): RecentItem[] => {
    return state.recentItems;
};

export const selectRecentItems = (state: RecentItemState): HydratedRecentItem[] => {
    const result = selectRawRecentItems(state);
    /*  Eliminate the need for checking by appexchangeId by looping on
     *  selectListing multiple times
     */
    const hydratedRecentItem: HydratedRecentItem[] = [];

    if (result) {
        result.forEach((item) => {
            const listing = selectListing(listingStore.getState(), item.listingId);
            if (listing) {
                hydratedRecentItem.push({ listing: listing, timestamp: item.timestamp });
            }
        });
    }
    return hydratedRecentItem as HydratedRecentItem[];
};

export const selectWhereYouLeftOff = (state: RecentItemState): LeftOffRecentItem[] => {
    const result = selectRawRecentItems(state);
    if (result) {
        const listings = selectListings(
            listingStore.getState(),
            result.map((item) => item.listingId),
        );
        // @ts-expect-error * Automated Type Error Fix
        return listings?.map((listing) => {
            // @ts-expect-error * Automated Type Error Fix
            const logo = listing['listing/plugins/LogoSet']?.Logo;
            return {
                appExchangeId: listing.appExchangeId,
                oafId: listing.oafId,
                tzId: listing.tzId,
                external_id: listing.external_id,
                title: listing.name,
                description: listing.description,
                publisher: listing.publisher?.name,
                listingCategories: [],
                logos: [
                    {
                        logoType: 'Logo',
                        mediaId: logo ? generateMediaUrl(logo) : 'images/home/appy-left-off.png',
                    } as schema.oaf.LogoSetItem,
                ],
                listingUrl: `${generateBaseUrl()}/${Routes.LISTING}?listingId=${listing.external_id}`,
                type: listing.technology,
            };
        });
    }
    return [];
};

const getUniqueRecentItems = (recentItems: RecentItem[]) =>
    // @ts-expect-error * Automated Type Error Fix
    recentItems.reduce((uniqueItems, item) => {
        // @ts-expect-error * Automated Type Error Fix
        const equalItemFn = (otherItem) => otherItem.listingId === item.listingId;
        return uniqueItems.some(equalItemFn) ? uniqueItems : [...uniqueItems, item];
    }, []);

export const recentItemStore = createStore(
    initialState,

    (set: SetState<RecentItemState>, get: GetState<RecentItemState>) => ({
        async fetchRecentItems(userId?: string) {
            const existingRecentItems = selectRecentItems(get());
            const insufficientRecentItems = !existingRecentItems || existingRecentItems.length < 3;
            if (insufficientRecentItems) {
                // @ts-expect-error * Automated Type Error Fix
                let recentItemResult = await recentItemService.getRecentItems(userId);
                const userState = userStore.getState();
                const recentItemsCount = recentItemResult?.recentItems?.length || 0;
                const existingItemsCount = existingRecentItems?.length || 0;

                if (recentItemsCount + existingItemsCount < 3) {
                    const recentActivity = userState.recentActivity || [];
                    let recentItems: RecentItem[] = recentActivity.map((recentItem) => ({
                        listingId: recentItem,
                        timestamp: Date.now(),
                    }));

                    // @ts-expect-error * Automated Type Error Fix
                    const extraRecentItems: RecentItem[] = existingRecentItems.map((recentItem) => ({
                        listingId: recentItem.listing.oafId,
                        timestamp: Date.now(),
                    }));

                    const recentItemsFromCookie = recentItemResult?.recentItems || [];
                    recentItems = [...extraRecentItems, ...recentItems, ...recentItemsFromCookie];
                    await Promise.allSettled(recentItems.map((item) => listingStore.fetchListing(item.listingId)));

                    const uniqueRecentItems = getUniqueRecentItems(recentItems);

                    recentItemResult = {
                        // @ts-expect-error * Automated Type Error Fix
                        recentItems: uniqueRecentItems,
                    };
                }

                if (recentItemResult?.recentItems) {
                    const finalRecentItems = recentItemResult.recentItems.slice(0, 5);
                    // allSettled will catch and transform all the exceptions. We use it here to ignore them
                    await Promise.allSettled(finalRecentItems.map((item) => listingStore.fetchListing(item.listingId)));
                    set(function updateRecentItemResult(draft: RecentItemState) {
                        draft.recentItems = finalRecentItems;
                    });
                }
            }
        },
        async updateRecentItems(userId: string, recentItem: RecentItem) {
            const newRecentItems = await recentItemService.updateRecentItems(recentItem);
            try {
                await listingStore.fetchListing(recentItem.listingId);
            } catch {
                logger.warn(`Unable to fetch listing ${recentItem.listingId} for Recent Items`);
            }

            set(function updateRecentItemResult(draft: RecentItemState) {
                draft.recentItems = newRecentItems;
            });
            this.fetchRecentItems(userId);
        },
        reset() {
            set(function resetStore() {
                return { ...initialState };
            });
        },
    }),
    'recentItemStore',
    IS_DEVELOPMENT,
);
